import { showPrice, allocateDiscountToTaxBrackets } from "../../../utils/commonfunction";

export const getCombinedOrderItems = (orderDetail) => {
  const baseItems = Array.isArray(orderDetail?.ordered_items)
    ? orderDetail.ordered_items
    : [];
  const subOrderItems = Array.isArray(orderDetail?.subOrders)
    ? orderDetail.subOrders.flatMap((sub) =>
        Array.isArray(sub?.ordered_items) ? sub.ordered_items : []
      )
    : [];
  return [...baseItems, ...subOrderItems];
};

const roundEditSalesAmount = (value) => Number(showPrice(value));

export const calculateEditSalesTotals = (
  items,
  sourceOrderDetail,
  restaurantSettings = {}
) => {
  const safeItems = items || [];
  const safeOrderDetail = sourceOrderDetail || {};
  const safeRestaurantSettings = restaurantSettings || {};

  // Mirror the backend's `calculateOrderTotals` / `orderRecalculationCashier`
  // exactly: service_charge is computed from the *raw, unrounded* subtotal
  // (backend rounds subtotal/tax/service_charge only at the very end, via a
  // single `.toFixed(0)` pass over the whole result object, AFTER
  // service_charge has already been derived from the raw subtotal). Rounding
  // `subtotal` before computing service_charge would diverge from backend.
  // grand/great-grand totals are then derived from the rounded fields.
  const subtotal = safeItems.reduce(
    (total, item) => total + (Number(item?.price || 0) * Number(item?.quantity || 0)),
    0
  );
  const taxPrice = safeItems.reduce(
    (total, item) => total + (Number(item?.tax_price || 0) * Number(item?.quantity || 0)),
    0
  );
  const totalTax8 = safeItems.reduce((total, item) => {
    if (Number(item?.consumption_tax_rate) !== 8) return total;
    return total + ((Number(item?.price || 0) + Number(item?.tax_price || 0)) * Number(item?.quantity || 0));
  }, 0);
  const totalTax10 = safeItems.reduce((total, item) => {
    if (Number(item?.consumption_tax_rate) !== 10) return total;
    return total + ((Number(item?.price || 0) + Number(item?.tax_price || 0)) * Number(item?.quantity || 0));
  }, 0);
  const grandCostPrice = safeItems.reduce(
    (total, item) => total + (Number(item?.cost_price || 0) * Number(item?.quantity || 0)),
    0
  );

  const serviceChargePercentage = Number(safeOrderDetail?.service_charge_percentage || 0);
  const roundedSubtotal = roundEditSalesAmount(subtotal);
  const roundedTaxPrice = roundEditSalesAmount(taxPrice);
  const roundedTotalTax8 = roundEditSalesAmount(totalTax8);
  const roundedTotalTax10 = roundEditSalesAmount(totalTax10);
  const roundedGrandCostPrice = roundEditSalesAmount(grandCostPrice);
  // Raw (unrounded) service charge, deliberately based on `subtotal` rather
  // than `roundedSubtotal` — see comment above.
  const rawServiceCharge = safeRestaurantSettings?.is_service_charge_enabled
    ? (subtotal * serviceChargePercentage) / 100
    : 0;
  const serviceCharge = safeRestaurantSettings?.is_service_charge_enabled
    ? roundEditSalesAmount(rawServiceCharge)
    : 0;

  const grandTotal = roundedSubtotal + roundedTaxPrice;
  const greatGrandTotal = grandTotal + serviceCharge;

  // Mirror backend `calculateOrderTotals`: coupon and gift-card bases use the
  // tax-inclusive `greatGrandTotal` (subtotal + tax + serviceCharge). Japanese
  // consumption tax is levied on the price actually charged, so a 100%-off
  // coupon must zero out tax too — see backend WRDS-772.
  const couponCodeApplied = !!safeOrderDetail?.coupon_code_applied;
  const couponType = safeOrderDetail?.coupon_type;
  const couponDiscountPercentage = Number(safeOrderDetail?.coupon_discount || 0);
  const couponCodeAmount = couponCodeApplied
    ? couponType === "percentage"
      ? roundEditSalesAmount((greatGrandTotal * couponDiscountPercentage) / 100)
      : Math.min(Number(safeOrderDetail?.coupon_code_amount || 0), greatGrandTotal)
    : 0;
  const giftCardApplied = !!safeOrderDetail?.gift_card_applied;
  const giftCardAmount = giftCardApplied
    ? Math.min(
        Number(safeOrderDetail?.gift_card_amount || 0),
        Math.max(greatGrandTotal - couponCodeAmount, 0)
      )
    : 0;

  // Discount lands on the 10% dine-in bracket first, then spills into 8%.
  const discountedBrackets = allocateDiscountToTaxBrackets({
    totalTax8: roundedTotalTax8,
    totalTax10: roundedTotalTax10,
    discount: couponCodeAmount + giftCardAmount,
    taxRoundType: safeRestaurantSettings?.tax_when_register_product,
  });

  return {
    subtotal: roundedSubtotal,
    tax_price: roundedTaxPrice,
    grand_total: grandTotal,
    ...discountedBrackets,
    service_charge: serviceCharge,
    great_grand_total: greatGrandTotal,
    amount_after_discount: roundEditSalesAmount(
      greatGrandTotal - couponCodeAmount - giftCardAmount
    ),
    grand_cost_price: roundedGrandCostPrice,
    great_grand_cost_price: roundedGrandCostPrice,
    coupon_code_applied: couponCodeApplied,
    coupon_discount: safeOrderDetail?.coupon_discount,
    coupon_type: couponType,
    coupon_code_amount: roundEditSalesAmount(couponCodeAmount),
    gift_card_applied: giftCardApplied,
    gift_card_amount: roundEditSalesAmount(giftCardAmount),
  };
};
